import React from "react";
import moment from "moment-timezone";
import "./BookingRestrictionsCard.css";

// frontend/src/components/staff/BookingRestrictionsCard.js
//
// BOOKING-RESTRICT-003 — Surface A (Customer Detail summary-card badge).
// Renders below the ESIGN status card on /staff/customers/:customerId
// (or below the vitals strip when ESIGN absent), per Zuri spec §3.1.
//
// Spec sources (locked):
//   docs/design/booking-restrictions-admin-design.md (Zuri)
//     §3 — placement + 6 visual states (no-restrictions / indefinite /
//          until-date / expired / loading / error)
//     §6 — typography
//     §7 — color tokens
//     §9 — accessibility
//     §10.1 — Visual DoD checklist
//   docs/architecture/booking-restrictions-tech.md (Marcus)
//     §3.4 — `restrictionsSummary` shape (count, hasIndefinite, hasExpired,
//            effectiveUntil) — the BE-derived block we consume here.
//     §6 — Indistinguishability checklist (HARD RULE §).
//
// HARD RULE § — Customer-side ZERO presence (binding):
//   This component is staff-only. It MUST NEVER be imported into
//   /src/components/MemberProfile.js, /src/components/BookSession.js,
//   /src/components/member-dashboard/*, or any customer-facing surface.
//   The customer's mobile app and customer web frontend show NO badge,
//   NO card, NO banner about restriction state. Verified by Nia per
//   Scenario 17 of the story.
//
// Production-safety:
//   - Pure-presentational; no API calls.
//   - Reuses VitalCard chrome conventions (CUST-DETAIL-POLISH-002 §3.1).
//   - Single-revert posture: removing the import in CustomerProfile.js
//     un-mounts the card; Story 001 backend admin endpoints continue to
//     function via CLI / Postman.

const PHOENIX_TZ = "America/Phoenix";
const EXPIRES_SOON_DAYS = 7;

const formatExpiryDate = (iso) => {
  if (!iso) return "";
  const m = moment.tz(iso, PHOENIX_TZ);
  if (!m.isValid()) return "";
  return m.format("MMM D, YYYY");
};

const isWithinDays = (iso, days) => {
  if (!iso) return false;
  const target = moment.tz(iso, PHOENIX_TZ);
  if (!target.isValid()) return false;
  const now = moment.tz(PHOENIX_TZ);
  const diff = target.diff(now, "days", true);
  return diff > 0 && diff <= days;
};

/**
 * Derive the four-state vocabulary from `restrictionsSummary` per Zuri §3.2.
 *
 * Maps `restrictionsSummary` flags (count + hasIndefinite + hasExpired) to a
 * single state token. Returns 'none' when there is no active restriction
 * profile, 'indefinite' for `count > 0 && hasIndefinite`, 'until-date' for
 * `count > 0 && !hasIndefinite && !hasExpired`, 'expired' for hasExpired.
 *
 * Exported for unit-testing the state machine in isolation.
 */
export const deriveBadgeState = (restrictionsSummary) => {
  if (!restrictionsSummary) return "none";
  const count = Number(restrictionsSummary.count) || 0;
  if (count <= 0) return "none";
  if (restrictionsSummary.hasExpired) return "expired";
  if (restrictionsSummary.hasIndefinite) return "indefinite";
  return "until-date";
};

const SHIELD_WARN_ICON = (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
    <path
      d="M8 1l5.5 2.5v3.5C13.5 10.5 11 13.5 8 15c-3-1.5-5.5-4.5-5.5-8V3.5L8 1z"
      fill="currentColor"
    />
    <path
      d="M8 5.5v3"
      stroke="#ffffff"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="8" cy="11" r="0.8" fill="#ffffff" />
  </svg>
);

const CHECK_ICON = (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
    <circle cx="8" cy="8" r="7" fill="currentColor" />
    <path
      d="M5 8l2.2 2.2L11 6"
      stroke="#ffffff"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const CLOCK_ICON = (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
    <circle
      cx="8"
      cy="8"
      r="6.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M8 4.5V8l2.5 1.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

/**
 * Build the breakdown subtitle clause ("X suite blocked, Y time window")
 * with correct pluralization and zero-count suppression per Zuri §3.2.2 +
 * Scenario 3.
 */
const buildBreakdown = (suiteCount, windowCount) => {
  const parts = [];
  if (suiteCount > 0) {
    parts.push(`${suiteCount} suite${suiteCount === 1 ? "" : "s"} blocked`);
  }
  if (windowCount > 0) {
    parts.push(
      `${windowCount} time window${windowCount === 1 ? "" : "s"}`
    );
  }
  return parts.join(", ");
};

/**
 * BookingRestrictionsCard — staff-only summary-card badge for the
 * Customer Detail page.
 *
 * @param {object} props
 * @param {object} [props.restrictionsSummary] — see Marcus tech-doc §3.4.
 *   Shape: { count, hasIndefinite, hasExpired, effectiveUntil }. Null /
 *   undefined / { count: 0 } all render the "no restrictions" state per
 *   Zuri §3.2.1.
 * @param {object} [props.bookingRestrictions] — full sub-doc (read via the
 *   customer-detail response when present). Used to derive the suite/
 *   time-window breakdown counts and the lastModifiedAt provenance line.
 *   Optional; the card degrades to summary-only when absent.
 * @param {boolean} [props.loading=false] — Renders the skeleton state per
 *   Zuri §3.2.5.
 * @param {boolean} [props.error=false] — Renders the error placeholder per
 *   Zuri §3.2.6.
 * @param {Function} [props.onOpenManage] — Click handler for the primary
 *   CTA. Opens Surface B (the management modal). Required when there are
 *   restrictions OR when the admin wants to add a first one.
 */
const BookingRestrictionsCard = ({
  restrictionsSummary,
  bookingRestrictions,
  loading = false,
  error = false,
  onOpenManage,
}) => {
  if (loading) {
    return (
      <section
        className="booking-restrictions-card booking-restrictions-card--loading"
        aria-label="Booking restrictions for this customer"
        aria-busy="true"
        role="status"
        data-testid="booking-restrictions-card-loading"
      >
        <div className="booking-restrictions-card__label-skeleton" />
        <div className="booking-restrictions-card__row-skeleton" />
        <div className="booking-restrictions-card__row-skeleton booking-restrictions-card__row-skeleton--narrow" />
      </section>
    );
  }

  if (error) {
    return (
      <section
        className="booking-restrictions-card booking-restrictions-card--error"
        aria-label="Booking restrictions for this customer"
        data-testid="booking-restrictions-card-error"
      >
        <span className="booking-restrictions-card__label">
          BOOKING RESTRICTIONS
        </span>
        <div className="booking-restrictions-card__row">
          <span className="booking-restrictions-card__value">—</span>
        </div>
        <p className="booking-restrictions-card__error-helper">
          Couldn&rsquo;t load restrictions. Refresh to try again.
        </p>
      </section>
    );
  }

  const state = deriveBadgeState(restrictionsSummary);
  const count = Number(restrictionsSummary?.count) || 0;
  const suiteCount = Array.isArray(bookingRestrictions?.blockedSuiteIds)
    ? bookingRestrictions.blockedSuiteIds.length
    : 0;
  const windowCount = Array.isArray(bookingRestrictions?.blockedTimeWindows)
    ? bookingRestrictions.blockedTimeWindows.length
    : 0;
  const lastModifiedAt = bookingRestrictions?.lastModifiedAt;
  const lastModifiedFormatted = lastModifiedAt
    ? moment.tz(lastModifiedAt, PHOENIX_TZ).format("MMM D, YYYY")
    : null;

  const handleClick = () => {
    if (typeof onOpenManage === "function") onOpenManage();
  };

  // §3.2.1 — No restrictions
  if (state === "none") {
    return (
      <section
        className="booking-restrictions-card booking-restrictions-card--none"
        aria-label="Booking restrictions for this customer"
        data-testid="booking-restrictions-card"
        data-state="none"
      >
        <span className="booking-restrictions-card__label">
          BOOKING RESTRICTIONS
        </span>
        <div className="booking-restrictions-card__row">
          <span
            className="booking-restrictions-card__icon booking-restrictions-card__icon--success"
            aria-hidden="true"
          >
            {CHECK_ICON}
          </span>
          <span className="booking-restrictions-card__primary">
            No restrictions
          </span>
          <button
            type="button"
            className="booking-restrictions-card__cta booking-restrictions-card__cta--outlined"
            onClick={handleClick}
            data-testid="booking-restrictions-card-cta-add"
          >
            Add restriction
          </button>
        </div>
      </section>
    );
  }

  // §3.2.2 / §3.2.3 / §3.2.4 — active states
  const breakdown = buildBreakdown(suiteCount, windowCount);
  const effectiveUntil =
    restrictionsSummary?.effectiveUntil ||
    bookingRestrictions?.effectiveUntil ||
    null;

  if (state === "expired") {
    const expiredDate = formatExpiryDate(effectiveUntil);
    return (
      <section
        className="booking-restrictions-card booking-restrictions-card--expired"
        aria-label="Booking restrictions for this customer (expired)"
        data-testid="booking-restrictions-card"
        data-state="expired"
      >
        <span className="booking-restrictions-card__label">
          BOOKING RESTRICTIONS
        </span>
        <div className="booking-restrictions-card__row">
          <span
            className="booking-restrictions-card__icon booking-restrictions-card__icon--muted"
            aria-hidden="true"
          >
            {CLOCK_ICON}
          </span>
          <span className="booking-restrictions-card__primary">
            <span className="booking-restrictions-card__count-strikethrough">
              {`${count} restriction${count === 1 ? "" : "s"}`}
            </span>{" "}
            (expired)
          </span>
          <button
            type="button"
            className="booking-restrictions-card__cta booking-restrictions-card__cta--outlined"
            onClick={handleClick}
            data-testid="booking-restrictions-card-cta-review"
          >
            Review or remove
          </button>
        </div>
        <p className="booking-restrictions-card__subtitle">
          {expiredDate
            ? `Expired ${expiredDate} MST.`
            : "Expired."}
          {breakdown ? ` ${breakdown} on file.` : ""}
        </p>
      </section>
    );
  }

  // §3.2.2 (indefinite) and §3.2.3 (until-date) share visual treatment;
  // primary text differs.
  const isIndefinite = state === "indefinite";
  const expiryFormatted = isIndefinite ? null : formatExpiryDate(effectiveUntil);
  const expiresSoon =
    !isIndefinite && isWithinDays(effectiveUntil, EXPIRES_SOON_DAYS);

  const primaryText = isIndefinite
    ? `${count} restriction${count === 1 ? "" : "s"} (indefinite)`
    : `${count} restriction${count === 1 ? "" : "s"} (until ${expiryFormatted})`;

  const subtitleClauses = [];
  if (breakdown) subtitleClauses.push(`${breakdown}.`);
  if (lastModifiedFormatted) {
    subtitleClauses.push(`Last updated ${lastModifiedFormatted} MST.`);
  }

  return (
    <section
      className={`booking-restrictions-card booking-restrictions-card--active booking-restrictions-card--${state}`}
      aria-label="Booking restrictions for this customer (active)"
      data-testid="booking-restrictions-card"
      data-state={state}
    >
      <span className="booking-restrictions-card__label">
        BOOKING RESTRICTIONS
      </span>
      <div className="booking-restrictions-card__row">
        <span
          className="booking-restrictions-card__icon booking-restrictions-card__icon--warn"
          aria-hidden="true"
        >
          {SHIELD_WARN_ICON}
        </span>
        <span className="booking-restrictions-card__primary">
          {primaryText}
        </span>
        <button
          type="button"
          className="booking-restrictions-card__cta booking-restrictions-card__cta--filled"
          onClick={handleClick}
          data-testid="booking-restrictions-card-cta-manage"
        >
          Manage
        </button>
      </div>
      <p className="booking-restrictions-card__subtitle">
        {subtitleClauses.join(" ")}
        {expiresSoon ? (
          <>
            {" "}
            <span className="booking-restrictions-card__expires-soon">
              (expires soon)
            </span>
          </>
        ) : null}
      </p>
    </section>
  );
};

export default BookingRestrictionsCard;
